// ---------------------------------------------------------------------------
// The Settings page's lists (R18b, from pages/settings.ts)
//
// On open, and after every change, the page asks six things at once: the
// Instantly and the Unipile mailboxes, the LinkedIn accounts, the Sales Reps,
// the CRM configuration and the CRM connections. The two kinds of mailbox
// become one list, each row saying which provider it belongs to. A list that
// did not load is reported as failed (R18a), never as empty; the page keeps
// what it showed of the reps and LinkedIn accounts and says the list failed.
// ---------------------------------------------------------------------------

import type { CrmConfig, CrmConnectionView } from '../crm-api.js';
import type { SalesRep } from '../wizard/types.js';
import { answerOf, type Send, viaFetch } from './settings-flow.js';
import { type SettingsLoadFailures, settingsLoadFailures } from './settings-safeguards.js';

export interface ConnectedEmail {
  /** The address for an Instantly mailbox; Unipile's account id for a Unipile one (what assign and disconnect take). */
  email: string;
  name: string;
  aiEmployeeId: string | null;
  totalSent?: number;
  /**
   * Which integration this mailbox belongs to.
   *
   * Both kinds appear in one list, and they are NOT interchangeable: assigning
   * or disconnecting hits a different provider for each, so a row that does not
   * carry its own provider gets acted on as an Instantly one. Absent means
   * Instantly, which is what every row was before Unipile email existed.
   */
  provider?: 'instantly_email' | 'unipile_email';
  /**
   * Whether an operator has marked this mailbox already-warmed, and the daily
   * send cap that follows (the 15/35/75 ramp day, or the full 100 when warmed).
   * Unipile mailboxes only — Instantly does its own warm-up and carries neither.
   */
  emailWarmedManually?: boolean;
  dailyLimit?: number;
}

export interface ConnectedLinkedin {
  id: string;
  name: string;
  status?: string;
  aiEmployeeId: string | null;
  totalInvitesSent?: number;
  totalDmsSent?: number;
  /**
   * `connection_params.im.premiumFeatures`, straight from the provider.
   *
   * Absent means LinkedIn did not say — which is NOT the same as an empty list,
   * and `describeSeat` keeps them apart. See linkedin-seat.ts.
   */
  premiumFeatures?: string[] | null;
}

/** HubSpot as the page shows it. */
export interface CrmConfigView {
  hubspot: { enabled: boolean; mode?: string; oauthAvailable?: boolean };
}

/** What the CRM section reads (crm-api.ts, over REST since R11b). */
export interface CrmReads {
  getCrmConfig(): Promise<CrmConfig>;
  listCrmConnections(): Promise<CrmConnectionView[]>;
}

/** Everything one load brought. */
export interface SettingsLists {
  emailAccounts: ConnectedEmail[];
  /** Null when the list did not load: the page keeps what it showed. */
  linkedinAccounts: ConnectedLinkedin[] | null;
  /** Null when the list did not load: the page keeps what it showed. */
  salesReps: SalesRep[] | null;
  crmConfig: CrmConfigView;
  crmConnections: CrmConnectionView[];
  loadFailed: SettingsLoadFailures;
}

/**
 * Both integrations feed one list, each row saying which it came from. A
 * Unipile mailbox is keyed by the provider's account id rather than by an
 * address: Instantly identifies a mailbox by its email, Unipile by an opaque
 * id, and the assign route reads whichever the row carries.
 */
export function mailboxesFrom(
  instantly: { ok?: boolean; accounts?: unknown },
  unipile: { ok?: boolean; accounts?: unknown },
): ConnectedEmail[] {
  const instantlyEmails: ConnectedEmail[] = instantly.ok ? ((instantly.accounts as ConnectedEmail[]) ?? []) : [];
  const unipileEmails: ConnectedEmail[] = unipile.ok
    ? ((unipile.accounts as any[]) ?? []).map((a: any) => ({
        email: a.id,
        name: a.email ?? a.displayName ?? a.id,
        aiEmployeeId: a.aiEmployeeId ?? null,
        totalSent: a.totalSent ?? 0,
        provider: 'unipile_email' as const,
        emailWarmedManually: a.emailWarmedManually === true,
        dailyLimit: a.dailyLimit,
      }))
    : [];
  return [...instantlyEmails, ...unipileEmails];
}

/** Asks the six sources at once; never throws for a source that failed. */
export async function loadSettingsLists(crm: CrmReads, send: Send = viaFetch): Promise<SettingsLists> {
  const [emailRes, unipileEmailRes, linkedinRes, aiRes, crmCfg, crmConns] = await Promise.all([
    answerOf(send('/api/accounts/email')),
    answerOf(send('/api/accounts/unipile-email')),
    answerOf(send('/api/accounts/linkedin')),
    answerOf(send('/api/ai-employees')),
    crm.getCrmConfig().catch(() => null),
    crm.listCrmConnections().catch(() => null),
  ]);
  return {
    emailAccounts: mailboxesFrom(emailRes, unipileEmailRes),
    linkedinAccounts: linkedinRes.ok ? (linkedinRes.accounts ?? []) : null,
    salesReps: aiRes.ok ? (aiRes.aiEmployees ?? []) : null,
    crmConfig: {
      hubspot: {
        enabled: !!crmCfg?.hubspot?.enabled,
        mode: crmCfg?.hubspot?.mode,
        oauthAvailable: !!crmCfg?.hubspot?.oauthAvailable,
      },
    },
    crmConnections: crmConns ?? [],
    // Each list's failure from its own answer (R18a; Instantly's left out on purpose).
    loadFailed: settingsLoadFailures({
      reps: aiRes,
      unipileEmail: unipileEmailRes,
      linkedin: linkedinRes,
      crmConfig: crmCfg,
      crmConnections: crmConns,
    }),
  };
}
